import {
  Accordion,
  Button,
  Code,
  Group,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Title
} from "@mantine/core";
import { DateTime } from "luxon";
import { useState } from "react";
import { useSearchParams } from "react-router";
import type { ConversationEntry } from "../../AiChat/ChatPanel/flattenConversationSnapshot";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import type { AiChatFeedbackRow } from "./hooks/useAiChatFeedback";
import { useAiChatFeedback } from "./hooks/useAiChatFeedback";

type RatingFilter = "all" | "up" | "down";

const RATING_FILTER_OPTIONS: Array<{ label: string; value: RatingFilter }> = [
  { label: "Alle", value: "all" },
  { label: "Up", value: "up" },
  { label: "Down", value: "down" }
];

const DEFAULT_RATING: RatingFilter = "down";
const DEFAULT_DAYS = 7;
// Kept in sync with DEFAULT_FEEDBACK_LIMIT / MAX_FEEDBACK_LIMIT in ne_ai_chat.router.
const DEFAULT_LIMIT = 100;
const MAX_LIMIT = 500;

function AiChatFeedback() {
  const [searchParams, setSearchParams] = useSearchParams();

  const rating =
    (searchParams.get("rating") as RatingFilter | null) ?? DEFAULT_RATING;
  const days = Number(searchParams.get("days") ?? DEFAULT_DAYS);
  const limit = Number(searchParams.get("limit") ?? DEFAULT_LIMIT);

  const [daysDraft, setDaysDraft] = useState<number | string>(days);
  const [limitDraft, setLimitDraft] = useState<number | string>(limit);
  const [ratingDraft, setRatingDraft] = useState<RatingFilter>(rating);

  const {
    data: feedback,
    isLoading,
    error
  } = useAiChatFeedback(rating, days, limit);

  const resultCount = feedback?.length ?? 0;
  const limitReached = resultCount === limit;

  function handleFilterSubmit() {
    setSearchParams({
      rating: ratingDraft,
      days: String(daysDraft || DEFAULT_DAYS),
      limit: String(limitDraft || DEFAULT_LIMIT)
    });
  }

  return (
    <Stack>
      <Title order={3}>opti.Agent Feedback</Title>

      <Group align="flex-end">
        <NumberInput
          label="Letzte N Tage"
          min={1}
          value={daysDraft}
          onChange={(value) => setDaysDraft(value)}
        />
        <Select
          data={RATING_FILTER_OPTIONS}
          label="Bewertung"
          value={ratingDraft}
          onChange={(value) =>
            setRatingDraft((value as RatingFilter) ?? DEFAULT_RATING)
          }
        />
        <NumberInput
          label="Max. Einträge"
          max={MAX_LIMIT}
          min={1}
          value={limitDraft}
          onChange={(value) => setLimitDraft(value)}
        />
        <Button onClick={handleFilterSubmit}>Filtern</Button>
      </Group>

      <LoadOrError error={error} loading={isLoading}>
        <Text>
          {resultCount} {resultCount === 1 ? "Ergebnis" : "Ergebnisse"}
        </Text>

        {limitReached && (
          <Text c="dimmed">
            Limit von {limit} erreicht — ältere Einträge fehlen möglicherweise.
            Erhöhe „Max. Einträge“ (max. {MAX_LIMIT}).
          </Text>
        )}

        {feedback && feedback.length > 0 ? (
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Erstellt am</Table.Th>
                <Table.Th>Bewertung</Table.Th>
                <Table.Th>Kommentar</Table.Th>
                <Table.Th>Benutzer-ID</Table.Th>
                <Table.Th>Nachricht-ID</Table.Th>
                <Table.Th>Unterhaltung</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {feedback.map((row) => (
                <FeedbackRow key={row.id} row={row} />
              ))}
            </Table.Tbody>
          </Table>
        ) : (
          <Text c="dimmed">Keine Ergebnisse.</Text>
        )}
      </LoadOrError>
    </Stack>
  );
}

function FeedbackRow({ row }: { row: AiChatFeedbackRow }) {
  // Top-align the cells only while the conversation is expanded; when collapsed
  // the short values look better vertically centered.
  const [expanded, setExpanded] = useState(false);
  const cellStyle = expanded ? { verticalAlign: "top" as const } : undefined;

  return (
    <Table.Tr>
      <Table.Td style={cellStyle}>{formatCreatedAt(row.created_at)}</Table.Td>
      <Table.Td style={cellStyle}>{row.rating}</Table.Td>
      <Table.Td style={cellStyle}>{row.comment ?? ""}</Table.Td>
      <Table.Td style={cellStyle}>{row.user_id}</Table.Td>
      <Table.Td style={cellStyle}>{row.message_id}</Table.Td>
      <Table.Td style={cellStyle}>
        <ConversationCell
          conversation={row.conversation as Array<ConversationEntry> | null}
          expanded={expanded}
          ratedMessageId={row.message_id}
          onExpandedChange={setExpanded}
        />
      </Table.Td>
    </Table.Tr>
  );
}

function ConversationCell({
  conversation,
  expanded,
  onExpandedChange,
  ratedMessageId
}: {
  conversation: Array<ConversationEntry> | null;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  ratedMessageId: string;
}) {
  if (!conversation || conversation.length === 0) {
    return <Text c="dimmed">—</Text>;
  }

  return (
    <Accordion
      value={expanded ? "conversation" : null}
      variant="separated"
      onChange={(value) => onExpandedChange(value === "conversation")}
    >
      <Accordion.Item value="conversation">
        <Accordion.Control>
          {conversation.length} Nachricht(en)
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="sm">
            {conversation.map((entry) => (
              <ConversationMessage
                entry={entry}
                isRated={entry.id === ratedMessageId}
                key={entry.id}
              />
            ))}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

function ConversationMessage({
  entry,
  isRated
}: {
  entry: ConversationEntry;
  isRated: boolean;
}) {
  return (
    <Stack
      bg={entry.role === "user" ? "blue.0" : "gray.1"}
      gap={4}
      p="xs"
      style={
        isRated
          ? {
              outline: "2px solid var(--mantine-color-yellow-6)",
              outlineOffset: 1
            }
          : undefined
      }
    >
      <Text c="dimmed" fw={700} fz="xs" tt="uppercase">
        {entry.role}
      </Text>
      <Text style={{ whiteSpace: "pre-wrap" }}>{entry.text}</Text>
      {entry.tools.length > 0 && (
        <Stack gap={4}>
          {entry.tools.map((tool, index) => (
            <ToolEntryDisplay key={`${tool.name}-${index}`} tool={tool} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function ToolEntryDisplay({
  tool
}: {
  tool: ConversationEntry["tools"][number];
}) {
  return (
    <Group gap="xs">
      <Text fw={700} fz="xs">
        {tool.name}
      </Text>
      {"failed" in tool && tool.failed ? (
        <Text c="red" fs="italic" fz="xs">
          fehlgeschlagen
        </Text>
      ) : (
        <Code block fz="xs">
          {JSON.stringify("output" in tool ? tool.output : undefined, null, 2)}
        </Code>
      )}
    </Group>
  );
}

function formatCreatedAt(createdAt: string) {
  return `${DateTime.fromISO(createdAt).toUTC().toFormat("yyyy-MM-dd HH:mm:ss")} UTC`;
}

export { AiChatFeedback };
